import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import MoreButton from 'components/MoreButton/MoreButton'
import { useGridState } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import { CompositeFilterDescriptor, resetGridFilters } from 'store/grid'
import { fetchFilterAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { getPlatformVersionFilterFields } from './platformVersionFields'
import { PlatformVersionModal } from './PlatformVersionModal'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

export interface PlatformVersion {
  id: string
  platform: NameDto
  version: NameDto<string>
  updateRequired: boolean
}

export interface CreatePlatformVersion {
  platformId: number
  versionId: string
  updateRequired: boolean
}

interface ModalProps {
  data?: PlatformVersion | null
  visible: boolean
}

const initialModalState = { data: null, visible: false }

export const PlatformVersions = () => {
  const crumbs = [{ title: 'Product - Platform Versions', link: links.productVersions.url }]
  const [editModal, setEditModal] = useState<ModalProps>(initialModalState)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const openEditModal = (data?: PlatformVersion) => setEditModal({ data, visible: true })

  const closeEditModal = () => setEditModal(initialModalState)

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [platformVersions, setPlatformVersions] = useState<PlatformVersion[]>()

  const { gridState, gridActions } = useGridState()

  const { filters } = useMapStateToProps()
  const { fetchFilters, resetGridFilters } = useMapDispatchToProps()

  useEffect(() => {
    resetGridFilters()
    if (isEmpty(filters)) fetchFilters()
  }, [])

  useEffect(() => {
    fetchVersions()
  }, [
    gridState.pageSize,
    gridState.pageIndex,
    gridState.take,
    tableData?.skipCount,
    gridState.sort.length,
  ])

  const fetchVersions = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const search = params?.query !== undefined ? params.query : getQuery()
      const pageIndex = gridState.pageIndex ?? 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = gridState.skip ?? 0
      const skipCount = tableData?.skipCount !== false

      const response = await clientAreaApiClient.getPlatformVersions({
        search,
        pageIndex,
        pageSize,
        skipCount,
      })

      if (response?.items) setPlatformVersions(response.items)
      else setPlatformVersions([])

      gridActions.setGridFilters({ take, skip, total: response.totalResults, pageIndex })
      setTableData({ ...tableData, meta: response })
    },
    [gridState, tableData]
  )

  const addVersion = async (data: CreatePlatformVersion) => {
    await clientAreaApiClient.addPlatformVersion(data)
    await fetchVersions()
  }

  const updateVersion = async (data: PlatformVersion) => {
    await clientAreaApiClient.updatePlatformVersion(data)
    await fetchVersions()
  }

  const handleEdit = async (data: PlatformVersion) => {
    if (data.id) await updateVersion(data)
    else
      await addVersion({
        platformId: data.platform.id,
        versionId: data.version.id,
        updateRequired: data.updateRequired,
      })
    closeEditModal()
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      if (platformVersions === undefined) return null
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const handleSkipCountChange = async (skipCount: boolean) =>
    setTableData({ ...tableData, skipCount })

  const handlePageSizeChange = async (pageSize: number) =>
    gridActions.setGridFilters({ ...gridState, pageIndex: 1, pageSize, take: pageSize })

  const handleGridPageChange = async (pageIndex: number) =>
    gridActions.setGridFilters({ pageIndex })

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={fetchVersions}
        filterType={FilterType.PlatformVersions}
        gridState={gridState}
        filterFields={getPlatformVersionFilterFields()}
      />

      {editModal.visible && (
        <PlatformVersionModal
          isOpen={editModal.visible}
          onClose={() => setEditModal(initialModalState)}
          data={editModal.data}
          handleEdit={handleEdit}
        />
      )}

      <AddButton
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditPlatformVersions)}
        text='Add New Platform Version'
        onClick={() => openEditModal()}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={platformVersions}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          <GridColumn width={250} title='Version' field='version.name' />
          <GridColumn width={250} title='Platform' field='platform.name' />
          <GridColumn width={250} title='Forced Update' field='updateRequired' />

          <GridColumn
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      disabled: !hasPermission(
                        permissions,
                        PermissionsEnum.CanEditPlatformVersions
                      ),
                      onClick: () => openEditModal(gridCellProps.dataItem),
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}
